import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BusinessHours } from '../features/settings/utils/businessHours';
import { trackEvent } from '../lib/analytics';
import { settingsAPI } from '../lib/api';
import { useMixpanel } from '../lib/mixpanel';

export interface OrgSettings {
  id: string;
  organizationId: string;
  boardVisibility: 'individual' | 'shared' | 'hybrid';
  // GET /organizations/:id/settings returns the full OrganizationSettings row
  // (no select filter) — leadInviteEnabled was already reachable here, just
  // never declared. NULL means off, same convention as every nullable opt-in
  // column in this codebase — never treat absence as permission.
  leadInviteEnabled: boolean | null;
  // Free-form org config bag; businessHours (FLOSEAI-690) is one of its keys.
  settingsOverrides?: Record<string, unknown> & { businessHours?: BusinessHours };
  createdAt: string;
  updatedAt: string;
}

export function useOrgSettings(organizationId: string | undefined) {
  return useQuery<OrgSettings>({
    queryKey: ['orgSettings', organizationId],
    queryFn: async () => {
      const { data } = await settingsAPI.getOrgSettings(organizationId!);
      return data;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export type UpdateOrgSettingsPayload = Parameters<typeof settingsAPI.updateOrgSettings>[1];

// Forwards whichever settings are given and names them in analytics: every org
// setting after boardVisibility had to widen a single-field version of this.
export function useUpdateOrgSettings() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation<OrgSettings, Error, { orgId: string } & UpdateOrgSettingsPayload>({
    mutationFn: async ({ orgId, ...payload }) => {
      const { data } = await settingsAPI.updateOrgSettings(orgId, payload);
      return data;
    },
    onSuccess: (_data, { orgId, ...payload }) => {
      trackEvent(mp, 'org.settings_updated', {
        setting: Object.keys(payload).sort().join(','),
      });
      queryClient.invalidateQueries({ queryKey: ['orgSettings', orgId] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });
}
